import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useT } from "@/lib/i18n";
import { AUTH } from "@/constants/testIds";
import { toast, Toaster } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const { t } = useT();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success(t("auth.signedIn"));
      navigate(next);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      let msg;
      if (status === 401) {
        msg = t("auth.invalid");
      } else if (detail) {
        msg = typeof detail === "string" ? detail : JSON.stringify(detail);
      } else if (status) {
        msg = `Erreur ${status}`;
      } else {
        msg = `${err?.message || "Erreur réseau"} — backend injoignable`;
      }
      toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-md mx-auto px-5 md:px-10 py-16 md:py-24">
      <Toaster theme="light" />
      <span className="chip-pink">{t("auth.login.kicker")}</span>
      <h1 className="font-display text-4xl text-[#1F3A5F] tracking-tight mt-4 font-medium mb-8">{t("auth.login.title")}</h1>
      <form onSubmit={submit} className="space-y-5 p-6 md:p-8 rounded-2xl border border-[#1F3A5F]/10 bg-white shadow-sm">
        <Field label={t("auth.email")}>
          <input data-testid={AUTH.email} type="email" required className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field label={t("auth.password")}>
          <input data-testid={AUTH.password} type="password" required className={inputCls} value={password} onChange={(e) => setPassword(e.target.value)} />
        </Field>
        <button data-testid={AUTH.submit} disabled={busy} className="w-full px-6 py-3 rounded-xl bg-[#E5276A] hover:bg-[#C01661] disabled:opacity-50 text-white text-base font-medium transition-colors">
          {busy ? "…" : t("auth.submit.login")}
        </button>
      </form>
      <p className="mt-6 text-sm text-[#4A5568] text-center">
        <Link to="/forgot-password" data-testid="auth-forgot-link" className="text-[#4A5568] hover:text-[#E5276A] underline">
          {t("auth.forgot")}
        </Link>
      </p>
      <p className="mt-3 text-sm text-[#4A5568] text-center">
        <Link to="/register" data-testid={AUTH.toggle} className="text-[#E5276A] hover:underline font-medium">{t("auth.toRegister")}</Link>
      </p>
    </div>
  );
}

const inputCls = "w-full bg-white border border-[#1F3A5F]/15 focus:border-[#E5276A] focus:ring-2 focus:ring-[#E5276A]/10 outline-none rounded-lg px-4 py-3 text-sm text-[#1F3A5F] placeholder:text-[#8B95A1]";
const Field = ({ label, children }) => (
  <label className="flex flex-col gap-1.5">
    <span className="text-[11px] uppercase tracking-[0.18em] text-[#E5276A] font-semibold">{label}</span>
    {children}
  </label>
);
